import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'

import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'

import axios from '../../dataClient/dataClient'
import { BoldBodyText } from '../styles/Text'
import { foiStatuses, completedFoiStatuses, statusTypeMap } from '../../util/foiConsts'
import { IconButton, SecondaryIconButton } from '../common/Button'
import { FlexRow, Form } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import { SelectMenuItem } from '../styles/TextField'
import FormSelect from '../inputFields/FormSelect'
import FOIStatus from './FOIStatus'

const FieldWrapper = styled.div`
  width: 185px;
`

const StatusContainer = styled(FlexRow)`
  align-items: center;
`

const CenteredRow = styled(FlexRow)`
  align-items: center;
`

const inProgressStatuses = foiStatuses.filter(s => !completedFoiStatuses.includes(s) && s !== 'NEW' && s !== 'AWAITING_APPLICATION_FEE')

const schema = yup.object().shape({
  status: yup.string().oneOf(inProgressStatuses).required('Status is required')
})

function FOIEditableStatus({ foiId, status, onUpdateStatus = () => {} }) {
  const [isEditing, setIsEditing] = useState(false)
  const [updatedStatus, setUpdatedStatus] = useState(status)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    setUpdatedStatus(status)
  }, [status])

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { status: updatedStatus }
  })
  const { reset, handleSubmit } = methods

  const toggleEdit = () => setIsEditing(isEditing => !isEditing)
  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit(payload) {
    setLoading(true)

    axios
      .patch(`/foi/${foiId}`, { status: payload.status }, { headers: { 'Content-Type': 'application/json' } })
      .then(({ data }) => {
        setUpdatedStatus(data.data.status)
        toggleEdit()
        setLoading(false)
        onUpdateStatus(data.data.status)
      })
      .catch(e => {
        setError(true)
        console.error('Failed to update the internal summary', e)
        setLoading(false)
      })
  }

  function onCancel() {
    reset({ status: updatedStatus })
    toggleEdit()
  }

  const canEditStatus = !completedFoiStatuses.includes(updatedStatus) && updatedStatus !== 'NEW' && updatedStatus !== 'AWAITING_APPLICATION_FEE'

  return (
    <StatusContainer gap="10px" width="100%">
      {!isEditing ? (
        <>
          <BoldBodyText>STATUS</BoldBodyText>
          <FOIStatus status={updatedStatus} />
          {canEditStatus && (
            <SecondaryIconButton onClick={toggleEdit} aria-label="Edit status">
              <EditIcon />
            </SecondaryIconButton>
          )}
        </>
      ) : (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onError)} gap="10px">
            <CenteredRow gap="10px">
              <BoldBodyText id="status-label">STATUS</BoldBodyText>
              <FieldWrapper>
                <FormSelect
                  id="status"
                  props={{ 'aria-labelledby': 'status-label' }}
                  options={inProgressStatuses}
                  defaultValue={updatedStatus}
                  disabled={loading}>
                  {inProgressStatuses.map(s => (
                    <SelectMenuItem key={s} value={s}>
                      {statusTypeMap[s].label}
                    </SelectMenuItem>
                  ))}
                </FormSelect>
              </FieldWrapper>
              <IconButton type="submit" loading={loading} aria-label="Save status">
                <SaveIcon />
              </IconButton>
              <SecondaryIconButton onClick={onCancel} disabled={loading} aria-label="Cancel">
                <CloseIcon />
              </SecondaryIconButton>
            </CenteredRow>
            {error && <ErrorMessage>Error updating status. Please try again.</ErrorMessage>}
          </Form>
        </FormProvider>
      )}
    </StatusContainer>
  )
}

FOIEditableStatus.propTypes = {
  foiId: PropTypes.string.isRequired,
  status: PropTypes.oneOf(foiStatuses).isRequired,
  onUpdateStatus: PropTypes.func
}

export default FOIEditableStatus
